import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RequestsTable from './components/RequestsTable';
import RequestDetails from './components/RequestDetails';
import CreateRequestModal from './components/CreateRequestModal';
import PendingApprovals from './components/PendingApprovals';
import StatusOverview from './components/StatusOverview';

const ChangeManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('requests');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Mock current user
  const currentUser = {
    id: 1,
    name: "John Doe",
    email: "john.doe@company.com",
    role: "admin"
  };

  // Clean start - no change requests
  const [changeRequests, setChangeRequests] = useState([]);

  // Clean start - no documents initially
  const documents = [
    { id: 1, name: "Employee Handbook v3.2" },
    { id: 2, name: "IT Security Policy" },
    { id: 3, name: "Quality Management System v2.1" },
    { id: 4, name: "Travel Expense Policy" },
    { id: 5, name: "Code of Conduct" },
    { id: 6, name: "Data Privacy Policy" },
    { id: 7, name: "Health & Safety Manual" },
    { id: 8, name: "Procurement Guidelines" }
  ];

  // Initialize with empty change requests
  useEffect(() => {
    setChangeRequests([]);
  }, []);

  const handleMenuToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleRequestSelect = (request) => {
    setSelectedRequest(request);
  };

  const handleCreateRequest = (requestData) => {
    const newRequest = {
      ...requestData,
      id: changeRequests?.length + 1,
      requestId: `CR-2024-${String(changeRequests?.length + 1)?.padStart(3, '0')}`,
      history: [
        {
          user: currentUser?.name,
          action: "Submitted change request",
          timestamp: new Date()?.toISOString()
        }
      ]
    };

    setChangeRequests(prev => [newRequest, ...prev]);
    setShowCreateModal(false);
    setSelectedRequest(newRequest);
  };

  const handleApprove = (requestId) => {
    setChangeRequests(prev => prev?.map(request => 
      request?.id === requestId 
        ? {
            ...request,
            status: 'approved',
            history: [
              ...request?.history,
              {
                user: currentUser?.name,
                action: "Approved change request",
                timestamp: new Date()?.toISOString(),
                comment: "Changes approved and ready for implementation"
              }
            ]
          }
        : request
    ));
    
    if (selectedRequest?.id === requestId) {
      setSelectedRequest(prev => ({
        ...prev,
        status: 'approved'
      }));
    }
  };

  const handleReject = (requestId) => {
    setChangeRequests(prev => prev?.map(request => 
      request?.id === requestId 
        ? {
            ...request,
            status: 'rejected',
            history: [
              ...request?.history,
              {
                user: currentUser?.name,
                action: "Rejected change request",
                timestamp: new Date()?.toISOString(),
                comment: "Changes require revision before approval"
              }
            ]
          }
        : request
    ));
    
    if (selectedRequest?.id === requestId) {
      setSelectedRequest(prev => ({
        ...prev,
        status: 'rejected'
      }));
    }
  };

  const handleAssignReviewer = (requestId, reviewerName) => {
    setChangeRequests(prev => prev?.map(request => 
      request?.id === requestId 
        ? {
            ...request,
            reviewer: reviewerName,
            status: 'in-review',
            history: [
              ...request?.history,
              {
                user: currentUser?.name,
                action: `Assigned ${reviewerName} as reviewer`,
                timestamp: new Date()?.toISOString()
              }
            ]
          }
        : request
    ));
  };

  const filteredRequests = statusFilter === 'all' 
    ? changeRequests 
    : changeRequests?.filter(request => request?.status === statusFilter);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'requests', label: 'All Requests', icon: 'FileText' },
    { id: 'pending', label: 'Pending Approvals', icon: 'Clock' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Change Management</h1>
              <p className="text-muted-foreground mt-1">
                Manage document modification workflows and approval processes
              </p>
            </div>
            <Button
              iconName="Plus"
              iconSize={16}
              onClick={() => setShowCreateModal(true)}
            >
              Create Request
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6 border-b border-border">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <StatusOverview requests={changeRequests} />
          )}

          {activeTab === 'requests' && (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2">
                <RequestsTable
                  requests={filteredRequests}
                  onRequestSelect={handleRequestSelect}
                  selectedRequest={selectedRequest}
                  onStatusFilter={setStatusFilter}
                  statusFilter={statusFilter}
                />
              </div>
              <div>
                <RequestDetails
                  request={selectedRequest}
                  onClose={() => setSelectedRequest(null)}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onAssignReviewer={handleAssignReviewer}
                  currentUser={currentUser}
                />
              </div>
            </div>
          )}

          {activeTab === 'pending' && (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2">
                <PendingApprovals
                  requests={changeRequests}
                  onRequestSelect={handleRequestSelect}
                  onQuickApprove={handleApprove}
                  onQuickReject={handleReject}
                  currentUser={currentUser}
                />
              </div>
              <div>
                <RequestDetails
                  request={selectedRequest}
                  onClose={() => setSelectedRequest(null)}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onAssignReviewer={handleAssignReviewer}
                  currentUser={currentUser}
                />
              </div>
            </div>
          )}
        </div>
      </main>
      {/* Create Request Modal */}
      <CreateRequestModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateRequest}
        documents={documents}
      />
    </div>
  );
};

export default ChangeManagement;